import DialogCreateListing from "@/components/listing/dialog-create-listing";
import ListingActionsMenu from "@/components/listing/listing-actions-menu";
import ListingPopulatingBadge from "@/components/listing/listing-populating-badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useListings } from "@/contexts/listings-provider";
import { useTeams } from "@/contexts/teams-provider";
import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import { ListingDTO, ListingOwnerType } from "@shaarp/common";
import {
  Building2Icon,
  PlusIcon,
  UsersIcon,
} from "lucide-react";
import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";

export default function ListingsList({
  listings,
  listingId,
  defaultOwnerType,
}: {
  listings: ListingDTO[];
  listingId: string | undefined;
  defaultOwnerType?: ListingOwnerType;
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [dialogCreateListing, setDialogCreateListing] =
    useState<boolean>(false);

  const { isAdmin } = useTeams();
  const { populatingListingsIds } = useListings();

  const onListingCreated = (newListing: ListingDTO) => {
    setDialogCreateListing(false);
    navigate(`/listing/${newListing.id}`);
  };

  return (
    <>
      <div className="flex flex-col gap-2 p-0 pt-0">
        {(defaultOwnerType !== "shaarp" || isAdmin) && (
          <Button
            variant="outline"
            className="w-full"
            onClick={() => setDialogCreateListing(true)}
          >
            <PlusIcon className="w-4 h-4 mr-2" />
            {t("listing.page.list.createButton")}
          </Button>
        )}

        {listings.map((listing) => {
          const isSelected = listingId === listing.id;
          const count =
            listing.listingType === "employees"
              ? listing.employeesCount
              : listing.companiesCount;
          const hasAssignees =
            listing.assignees && listing.assignees.length > 0;
          // Still being filled: opening it now would show an empty list.
          const isPopulating = populatingListingsIds.has(listing.id);

          return (
            <NavLink
              key={listing.id}
              to={`/listing/${listing.id}`}
              aria-disabled={isPopulating}
              tabIndex={isPopulating ? -1 : undefined}
              className={cn(
                "block rounded-lg border border-border bg-background px-[7px] py-[9px] text-left text-sm transition-all duration-200 ease-out hover:bg-accent overflow-hidden ring-0 ring-inset ring-majorelle",
                isSelected && "border-majorelle ring-1",
                isPopulating && "pointer-events-none opacity-60",
              )}
            >
              <div className="flex gap-1 items-center w-full">
                <div className="flex justify-center items-center w-7 h-7 rounded-md shrink-0 bg-muted/70">
                  {listing.listingType === "employees" ? (
                    <UsersIcon className="w-4 h-4 text-muted-foreground" />
                  ) : (
                    <Building2Icon className="w-4 h-4 text-muted-foreground" />
                  )}
                </div>
                <div className="flex-1 min-w-0 font-semibold truncate">
                  {listing.title}
                </div>
                <div className="flex gap-1 items-center shrink-0">
                  {isPopulating ? (
                    <ListingPopulatingBadge className="mx-1.5" />
                  ) : (
                    <>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Badge
                            className={cn(
                              "px-2 py-0.5 text-xs font-medium rounded-full border-transparent transition-colors duration-200 ease-out",
                              isSelected
                                ? "bg-majorelle text-white hover:bg-majorelle"
                                : "bg-zinc-200 text-zinc-700 dark:bg-zinc-700 dark:text-zinc-200 hover:bg-zinc-200 dark:hover:bg-zinc-700",
                            )}
                          >
                            {count}
                          </Badge>
                        </TooltipTrigger>
                        <TooltipContent>
                          {listing.listingType === "employees"
                            ? t("listing.page.list.badges.employeesTooltip")
                            : t("listing.page.list.badges.companiesTooltip")}
                        </TooltipContent>
                      </Tooltip>
                      {defaultOwnerType === "user" && (
                        <ListingActionsMenu listing={listing} />
                      )}
                    </>
                  )}
                </div>
              </div>
              {hasAssignees && (
                <div className="flex gap-2 items-center pl-8 mt-1.5">
                  <span className="text-xs text-muted-foreground">
                    {t("listing.page.list.assignedTo")}
                  </span>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex -space-x-1.5">
                        {listing.assignees.slice(0, 3).map((assignee) => (
                          <Avatar
                            key={assignee.id}
                            className="w-5 h-5 border border-background shrink-0"
                          >
                            <AvatarImage src={assignee.imageUrl} />
                            <AvatarFallback className="text-[10px]">
                              {assignee.fullName?.charAt(0)}
                            </AvatarFallback>
                          </Avatar>
                        ))}
                        {listing.assignees.length > 3 && (
                          <Avatar className="w-5 h-5 border border-background shrink-0">
                            <AvatarFallback className="text-[10px]">
                              +{listing.assignees.length - 3}
                            </AvatarFallback>
                          </Avatar>
                        )}
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      {listing.assignees.map((a) => a.fullName).join(", ")}
                    </TooltipContent>
                  </Tooltip>
                </div>
              )}
            </NavLink>
          );
        })}
      </div>
      {dialogCreateListing && (
        <DialogCreateListing
          onClose={() => setDialogCreateListing(false)}
          onCreated={onListingCreated}
          defaultOwnerType={defaultOwnerType}
        />
      )}
    </>
  );
}
